import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const { FiMoreVertical, FiExternalLink, FiFilter, FiSearch, FiCheckCircle, FiClock, FiX, FiCalendar, FiRefreshCw, FiPlus, FiList, FiGrid, FiColumns, FiArchive, FiTrash2, FiPauseCircle } = FiIcons;

const Tracker = () => {
  const { session } = useAuth();
  const { data, refreshData } = useProfile();
  const mockApplications = data.applications;

  const [filter, setFilter] = useState(() => localStorage.getItem('appTrackerFilter') || 'All');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewType, setViewType] = useState(() => localStorage.getItem('appTrackerView') || 'grid');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showAddApp, setShowAddApp] = useState(false);
  const [newApp, setNewApp] = useState({ company: '', role: '', matchScore: 80 });
  const [statusUpdate, setStatusUpdate] = useState('');

  const statuses = ['All', 'Applied', 'Interviewing', 'Offer', 'Rejected', 'Archived', 'Postponed'];

  useEffect(() => {
    localStorage.setItem('appTrackerFilter', filter);
    localStorage.setItem('appTrackerView', viewType);
  }, [filter, viewType]);


  const updateAppStatus = async (id, newStatus) => {
    if (!session) return;
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', id)
        .eq('profile_id', session.user.id);
      if (error) throw error;
      if (selectedApp?.id === id) setSelectedApp({...selectedApp, status: newStatus});
      refreshData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDragStart = (e, appId) => {
    e.dataTransfer.setData('appId', appId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const appId = e.dataTransfer.getData('appId');
    if (appId) {
      const app = mockApplications.find(a => String(a.id) === String(appId));
      if (app && app.status !== newStatus) {
        await updateAppStatus(appId, newStatus);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const deleteApp = async (id) => {
    if (!session) return;
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', id)
        .eq('profile_id', session.user.id);
      if (error) throw error;
      if (selectedApp?.id === id) setSelectedApp(null);
      refreshData();
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const filteredApps = mockApplications.filter(app => {
    const matchesFilter = (viewType === 'kanban' || filter === 'All') || app.status === filter;
    const matchesSearch = app.company.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          app.role.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'Applied': return 'bg-blue-100 text-blue-700';
      case 'Interviewing': return 'bg-purple-100 text-purple-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Offer': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTimeline = (app) => {
    return app.timeline || [
      { id: 1, status: 'Applied', date: 'Oct 12', description: 'Application submitted via ATS', completed: true },
      { id: 2, status: 'Under Review', date: 'Oct 14', description: 'Recruiter viewed application', completed: true },
      { id: 3, status: 'Interviewing', date: 'Oct 20', description: 'Screening call with HR', completed: app.status === 'Interviewing' }
    ];
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 relative">

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Application Tracker</h1>
          <p className="text-gray-500 mt-1">Monitor the status and journey of your applications.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto overflow-x-auto hide-scrollbar">
          <div className="relative flex-1 md:flex-none md:w-64 min-w-[200px]">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Filter by company..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={() => setShowAddApp(true)} className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-all flex-shrink-0">
            <SafeIcon icon={FiPlus} /> Add App
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide w-full md:w-auto">
          {viewType !== 'kanban' && statuses.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                filter === s
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                  : 'bg-white text-gray-500 border border-gray-100 hover:border-blue-200'
              }`}
            >
              {s}
              <span className={`ml-2 px-1.5 py-0.5 rounded-md text-[10px] ${
                filter === s ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                {s === 'All' ? mockApplications.length : mockApplications.filter(a => a.status === s).length}
              </span>
            </button>
          ))}
        </div>

        <div className="bg-white p-1 rounded-xl border border-gray-100 shadow-sm flex ml-auto shrink-0">
             <button
                onClick={() => setViewType('list')}
                className={`p-2 rounded-lg transition-colors ${viewType === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'}`}
                title="List View"
             >
                <SafeIcon icon={FiList} size={18} />
             </button>
             <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-lg transition-colors ${viewType === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'}`}
                title="Grid View"
             >
                <SafeIcon icon={FiGrid} size={18} />
             </button>
             <button
                onClick={() => setViewType('kanban')}
                className={`p-2 rounded-lg transition-colors ${viewType === 'kanban' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'}`}
                title="Kanban View"
             >
                <SafeIcon icon={FiColumns} size={18} />
             </button>
        </div>
      </div>

      {viewType === 'list' && (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-sm font-medium text-gray-500">
              <th className="p-4 pl-6">Company & Role</th>
              <th className="p-4">Date Applied</th>
              <th className="p-4">Status</th>
              <th className="p-4">ATS Match</th>
              <th className="p-4 text-right pr-6">Timeline</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <AnimatePresence mode="popLayout">
              {filteredApps.length > 0 ? filteredApps.map((app) => (
                <motion.tr 
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={app.id} 
                  className="hover:bg-gray-50/50 transition-colors group cursor-pointer"
                  onClick={() => setSelectedApp(app)}
                >
                  <td className="p-4 pl-6">
                    <div className="font-semibold text-gray-900">{app.company}</div>
                    <div className="text-sm text-gray-500">{app.role}</div>
                  </td>
                  <td className="p-4 text-gray-600 text-sm">{new Date(app.date || app.created_at || Date.now()).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                      {app.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${(app.match_score || app.matchScore || 0) > 85 ? 'bg-green-500' : 'bg-yellow-500'}`}
                          style={{ width: `${app.match_score || app.matchScore || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{app.match_score || app.matchScore || 0}%</span>
                    </div>
                  </td>
                  <td className="p-4 pr-6 text-right">
                     <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                        <button onClick={() => updateAppStatus(app.id, 'Archived')} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Archive"><SafeIcon icon={FiArchive} size={16}/></button>
                        <button onClick={() => updateAppStatus(app.id, 'Postponed')} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Postpone"><SafeIcon icon={FiPauseCircle} size={16}/></button>
                        <button onClick={() => deleteApp(app.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><SafeIcon icon={FiTrash2} size={16}/></button>
                     </div>
                  </td>
                </motion.tr>
              )) : (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-gray-400">
                    No applications found matching your criteria.
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
      )}

      {viewType === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filteredApps.length > 0 ? filteredApps.map((app) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                key={app.id}
                onClick={() => setSelectedApp(app)}
                className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer relative group flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{app.company}</h3>
                    <p className="text-sm text-gray-500 font-medium">{app.role}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(app.status)}`}>
                    {app.status}
                  </span>
                </div>

                <div className="space-y-3 mt-auto pt-4 border-t border-gray-50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center gap-2"><SafeIcon icon={FiCalendar} className="text-gray-400" /> Date</span>
                    <span className="font-semibold text-gray-700">{new Date(app.date || app.created_at || Date.now()).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center gap-2"><SafeIcon icon={FiCheckCircle} className="text-blue-400" /> Match</span>
                    <span className="font-bold text-blue-600">{app.match_score || app.matchScore || 0}%</span>
                  </div>
                </div>

                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-1.5 flex gap-1 items-center" onClick={e => e.stopPropagation()}>
                    <button onClick={() => updateAppStatus(app.id, 'Archived')} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Archive"><SafeIcon icon={FiArchive} size={14}/></button>
                    <button onClick={() => updateAppStatus(app.id, 'Postponed')} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Postpone"><SafeIcon icon={FiPauseCircle} size={14}/></button>
                    <button onClick={() => deleteApp(app.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><SafeIcon icon={FiTrash2} size={14}/></button>
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full py-12 text-center text-gray-400">
                No applications found matching your criteria.
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      {viewType === 'kanban' && (
        <div className="flex overflow-x-auto gap-6 pb-6 hide-scrollbar snap-x items-start">
          {statuses.filter(s => ['Applied', 'Interviewing', 'Offer'].includes(s)).map(status => {
            const statusApps = filteredApps.filter(app => app.status === status);
            return (
              <div key={status} className="w-80 flex-shrink-0 bg-gray-50/50 rounded-2xl p-4 border border-gray-100 flex flex-col h-[75vh] snap-center" onDrop={(e) => { e.stopPropagation(); handleDrop(e, status); }} onDragOver={handleDragOver}>
                <div className="flex justify-between items-center mb-4 px-1">
                  <h3 className="font-bold text-gray-800">{status}</h3>
                  <span className="bg-white text-gray-500 text-xs font-bold px-2.5 py-1 rounded-lg shadow-sm border border-gray-100">{statusApps.length}</span>
                </div>
                <div className="overflow-y-auto space-y-3 flex-1 custom-scrollbar pr-1 pb-2">
                  <AnimatePresence>
                    {statusApps.map(app => (
                        <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        key={app.id}
                        onClick={() => setSelectedApp(app)}
                        className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all cursor-pointer relative group" draggable onDragStart={(e) => handleDragStart(e, app.id)} onDragEnd={(e) => e.preventDefault()} onDragOver={handleDragOver} onDrop={(e) => { e.stopPropagation(); handleDrop(e, status); }}
                      >
                        <h4 className="font-bold text-gray-900 text-sm">{app.company}</h4>
                        <p className="text-xs text-gray-500 mb-4 font-medium">{app.role}</p>

                        <div className="flex justify-between items-center text-[10px] font-bold">
                          <span className="text-gray-400 uppercase tracking-wider">{new Date(app.date || app.created_at || Date.now()).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</span>
                          <span className="text-blue-600 bg-blue-50 px-2 py-1 rounded-md">{app.match_score || app.matchScore || 0}% Match</span>
                        </div>

                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-50 p-1 flex items-center" onClick={e => e.stopPropagation()}>
                            <button onClick={() => updateAppStatus(app.id, 'Archived')} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Archive"><SafeIcon icon={FiArchive} size={12}/></button>
                            <button onClick={() => updateAppStatus(app.id, 'Postponed')} className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded" title="Postpone"><SafeIcon icon={FiPauseCircle} size={12}/></button>
                            <button onClick={() => deleteApp(app.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><SafeIcon icon={FiTrash2} size={12}/></button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {statusApps.length === 0 && (
                    <div className="text-center py-8 text-xs font-medium text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                      No applications
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      <AnimatePresence>
        {selectedApp && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedApp(null)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[450px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >

              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-black text-gray-900">{selectedApp.company}</h2>
                  <p className="text-blue-600 font-bold">{selectedApp.role}</p>
                </div>
                <button onClick={() => setSelectedApp(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-xl text-gray-400" />
                </button>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => updateAppStatus(selectedApp.id, 'Archived')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  <SafeIcon icon={FiArchive} /> Archive
                </button>
                <button
                   onClick={() => updateAppStatus(selectedApp.id, 'Postponed')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
                >
                  <SafeIcon icon={FiPauseCircle} /> Postpone
                </button>
                <button
                  onClick={() => deleteApp(selectedApp.id)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors ml-auto"
                >
                  <SafeIcon icon={FiTrash2} /> Delete
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl mb-8 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase">Current Stage</p>
                  <p className="font-bold text-gray-900">{selectedApp.status}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase">Match Score</p>
                  <p className="text-2xl font-black text-blue-600 leading-none">{selectedApp.match_score || selectedApp.matchScore || 0}%</p>
                </div>
              </div>
<h3 className="font-black text-gray-900 mb-6 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="text-blue-500" /> Application Timeline
              </h3>

              <div className="space-y-8 relative before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                {getTimeline(selectedApp).map((event) => (
                  <div key={event.id} className="relative pl-12">
                    <div className={`absolute left-0 top-1 w-9 h-9 rounded-full flex items-center justify-center z-10 border-4 border-white shadow-sm ${
                      event.completed ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <SafeIcon icon={event.completed ? FiCheckCircle : FiClock} />
                    </div>
                    <div>
                      <div className="flex justify-between items-start">
                        <h4 className={`font-bold ${event.completed ? 'text-gray-900' : 'text-gray-400'}`}>{event.status}</h4>
                        <span className="text-[10px] font-black text-gray-400 uppercase">{event.date}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-gray-100">
                <select
                  className="w-full mb-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                >
                  <option value="">Select new status...</option>
                  <option value="Applied">Applied</option>
                  <option value="Interviewing">Interviewing</option>
                  <option value="Offer">Offer</option>
                  <option value="Rejected">Rejected</option>
                </select>
                <button
                  onClick={async () => {
                    if (!statusUpdate || !selectedApp) return;
                    const { error } = await supabase
                      .from('applications')
                      .update({ status: statusUpdate, updated_at: new Date().toISOString() })
                      .eq('id', selectedApp.id);
                    if (!error) {
                      setStatusUpdate('');
                      setSelectedApp({ ...selectedApp, status: statusUpdate });
                      refreshData();
                    }
                  }}
                  className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                >
                  Update Status <SafeIcon icon={FiRefreshCw} />
                </button>
                <button onClick={() => alert("Job Details view coming soon!")} className="w-full mt-3 py-4 text-gray-500 font-bold hover:bg-gray-50 rounded-2xl transition-all">
                  View Job Details
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddApp && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddApp(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[450px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Track New Application</h3>
                <button onClick={() => setShowAddApp(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Company Name"
                  value={newApp.company}
                  onChange={(e) => setNewApp({...newApp, company: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Role (e.g. Frontend Engineer)"
                  value={newApp.role}
                  onChange={(e) => setNewApp({...newApp, role: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">ATS Match Score Estimate</label>
                  <input
                    type="range" min="0" max="100"
                    value={newApp.matchScore}
                    onChange={(e) => setNewApp({...newApp, matchScore: parseInt(e.target.value)})}
                    className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="text-right text-xs font-bold text-blue-600 mt-1">{newApp.matchScore}%</div>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowAddApp(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newApp.company || !newApp.role) return;
                    const { error } = await supabase
                      .from('applications')
                      .insert({
                        profile_id: session.user.id,
                        company: newApp.company,
                        role: newApp.role,
                        match_score: newApp.matchScore,
                        status: 'Applied'
                      });
                    if (!error) {
                      setNewApp({ company: '', role: '', matchScore: 80 });
                      setShowAddApp(false);
                      refreshData();
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  Save App
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tracker;